import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon, type IconName } from "./Icon";
import { Logo } from "./Logo";
import { WeddingLogo } from "./WeddingLogo";
import { useWedding } from "@/hooks/useWedding";
import { useAuth, signOut } from "@/hooks/useAuth";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { BILLING_ENABLED } from "@/features/billing/feature-flags";
import { useIsDemoUser } from "@/features/demo/useIsDemoUser";
import { leaveDemoApp } from "@/features/demo/leaveDemoApp";
import { FeedbackButton } from "@/features/feedback";
import { resetWebsiteOnboarding } from "@/features/website/onboarding/website-onboarding-storage";
import { toMillis } from "@/lib/timestamps";
import { FREE_GUEST_LIMIT } from "@amazing-wedding/shared-types";
import dayjs from "dayjs";

interface NavItem {
  id: string;
  to: string;
  labelKey: `nav.${string}`;
  icon: IconName;
  badge?: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: "dashboard", to: "/app/dashboard", labelKey: "nav.dashboard", icon: "home" },
  { id: "guests", to: "/app/guests", labelKey: "nav.guests", icon: "users" },
  { id: "seating", to: "/app/seating", labelKey: "nav.seating", icon: "table" },
  { id: "tasks", to: "/app/tasks", labelKey: "nav.tasks", icon: "list" },
  { id: "budget", to: "/app/budget", labelKey: "nav.budget", icon: "coin" },
  { id: "accommodation", to: "/app/accommodation", labelKey: "nav.accommodation", icon: "bed" },
  { id: "day-plan", to: "/app/day-plan", labelKey: "nav.dayPlan", icon: "clock" },
  { id: "papeteria", to: "/app/papeteria", labelKey: "nav.papeteria", icon: "papeteria" },
  { id: "website", to: "/app/website", labelKey: "nav.website", icon: "globe" },
];

function WeddingCardSkeleton({ label }: { label: string }) {
  return (
    <div className="sidebar-wedding-skeleton" aria-busy="true" aria-label={label}>
      <div className="skeleton-line skeleton-line--title" />
      <div className="skeleton-line skeleton-line--date" />
      <div className="skeleton-line skeleton-line--days" />
    </div>
  );
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { wedding, loading: weddingLoading } = useWedding();
  const { user, isAdmin } = useAuth();
  const { effectivePlan, isPremium } = useCurrentUser();
  const isDemoUser = useIsDemoUser();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [demoLeaveBusy, setDemoLeaveBusy] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!accountMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!accountMenuRef.current?.contains(e.target as Node)) setAccountMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAccountMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [accountMenuOpen]);

  const weddingMs = wedding ? toMillis(wedding.weddingDate) : 0;
  const hasWeddingDate = weddingMs > 0;
  const daysUntil = hasWeddingDate ? dayjs(weddingMs).diff(dayjs(), "day") : null;
  const formattedDate = hasWeddingDate ? dayjs(weddingMs).format("D MMMM YYYY") : "";
  const initials = user?.displayName?.[0]?.toUpperCase() ?? "A";

  // Free-tier progress bar in the wedding card — only surfaces once the
  // couple is past the half-way mark so it doesn't shout at early users.
  const attendees = wedding?.attendeeCount ?? 0;
  const usageRatio = Math.min(1, attendees / FREE_GUEST_LIMIT);
  const showFreeProgress = effectivePlan === "free" && wedding != null && usageRatio >= 0.5;
  const atLimit = usageRatio >= 1;

  const handleLeaveDemo = async () => {
    if (demoLeaveBusy) return;
    setDemoLeaveBusy(true);
    setAccountMenuOpen(false);
    onNavigate?.();
    try {
      await leaveDemoApp(navigate);
    } finally {
      setDemoLeaveBusy(false);
    }
  };

  const handleResetWebsiteOnboarding = () => {
    if (!wedding?.id) return;
    resetWebsiteOnboarding(wedding.id);
    setAccountMenuOpen(false);
    onNavigate?.();
    if (!location.pathname.startsWith("/app/website")) {
      navigate("/app/website");
    }
  };

  const showDevWebsiteOnboardingReset = import.meta.env.DEV && Boolean(wedding?.id);

  return (
    <aside className="sidebar">
      <div style={{ padding: "0 8px" }}>
        <NavLink
          to="/"
          aria-label={t("sidebar.homeAria")}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Logo size={32} textSize={24} />
        </NavLink>
      </div>
      <div>
        <div className="sidebar-section-label">{t("sidebar.section.wedding")}</div>
        <div
          style={{
            background: "var(--bg-2)",
            border: "1px solid var(--line)",
            borderRadius: "var(--r-md)",
            padding: "10px 12px",
            marginBottom: 16,
          }}
        >
          {weddingLoading ? (
            <WeddingCardSkeleton label={t("sidebar.weddingLoading")} />
          ) : wedding ? (
            <>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18, lineHeight: 1.1 }}>
                {wedding.coupleName1}{" "}
                <span style={{ fontStyle: "italic", color: "var(--accent)" }}>&</span>{" "}
                {wedding.coupleName2}
              </div>
              {hasWeddingDate ? (
                <div
                  className="t-mono"
                  style={{
                    marginTop: 6,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    lineHeight: 1.25,
                    transition: "opacity var(--t-med)",
                  }}
                >
                  <div>{formattedDate}</div>
                  <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                    {t("sidebar.daysUntil", { count: daysUntil ?? 0 })}
                  </div>
                </div>
              ) : (
                <NavLink to="/app/settings" className="t-mono sidebar-wedding-date-cta">
                  {t("sidebar.setWeddingDate")}
                </NavLink>
              )}
            </>
          ) : (
            <NavLink to="/app/settings" className="t-mono sidebar-wedding-date-cta">
              {t("sidebar.setWeddingDate")}
            </NavLink>
          )}
          {showFreeProgress && !isDemoUser && (
            <NavLink
              to="/app/upgrade"
              onClick={onNavigate}
              aria-label={t("sidebar.freePlanAria", {
                attendees,
                limit: FREE_GUEST_LIMIT,
              })}
              style={{
                display: "block",
                marginTop: 10,
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 10,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: atLimit ? "var(--danger)" : "var(--ink-3)",
                  fontFamily: "var(--font-mono)",
                  marginBottom: 4,
                }}
              >
                <span>{t("sidebar.planFree")}</span>
                <span>
                  {attendees}/{FREE_GUEST_LIMIT}
                </span>
              </div>
              <div
                style={{
                  height: 4,
                  borderRadius: 999,
                  background: "var(--bg-3)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${usageRatio * 100}%`,
                    background: atLimit ? "var(--danger)" : "var(--accent)",
                    transition: "width var(--t-med)",
                  }}
                />
              </div>
            </NavLink>
          )}
        </div>
        <div className="sidebar-section-label">{t("sidebar.section.workspace")}</div>
        <ul className="nav-list">
          <li>
            <NavLink
              to="/app/ai"
              className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
              style={{ textDecoration: "none" }}
              onClick={onNavigate}
            >
              <span className="nav-icon nav-icon-brand">
                <WeddingLogo size={22} />
              </span>
              {t("nav.ai")}
            </NavLink>
          </li>
          {NAV_ITEMS.map((item) => {
            const active = location.pathname.startsWith(item.to);
            return (
              <li key={item.id}>
                <NavLink
                  to={item.to}
                  className={"nav-item" + (active ? " active" : "")}
                  style={{ textDecoration: "none" }}
                  onClick={onNavigate}
                >
                  <span className="nav-icon">
                    <Icon name={item.icon} size={16} />
                  </span>
                  {t(item.labelKey)}
                  {item.badge && <span className="nav-badge">{item.badge}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
      <div style={{ marginTop: "auto" }}>
        {isDemoUser && (
          <div className="sidebar-demo-exit">
            <p className="sidebar-demo-exit-hint">{t("demo.leaveModeHint")}</p>
            <button
              type="button"
              className="btn btn-primary sidebar-leave-demo"
              aria-label={t("demo.leaveModeAria")}
              disabled={demoLeaveBusy}
              onClick={() => void handleLeaveDemo()}
            >
              <Icon name="logOut" size={16} />
              {demoLeaveBusy ? t("common.loading") : t("demo.leaveMode")}
            </button>
          </div>
        )}
        <div className="sidebar-section-label">{t("sidebar.section.tools")}</div>
        <ul className="nav-list">
          {BILLING_ENABLED && !isDemoUser && (
            <li>
              <NavLink
                to="/app/upgrade"
                className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
                style={{ textDecoration: "none" }}
                onClick={onNavigate}
              >
                <span className="nav-icon">
                  <Icon name="sparkle" />
                </span>
                {t("nav.upgrade")}{" "}
                {effectivePlan === "free" && (
                  <span
                    className="nav-badge"
                    style={{
                      background: "var(--accent)",
                      color: "var(--paper)",
                    }}
                  >
                    {t("sidebar.upgradeBadge")}
                  </span>
                )}
              </NavLink>
            </li>
          )}
          <li>
            <FeedbackButton onActivate={onNavigate} />
          </li>
          <li>
            <NavLink
              to="/app/settings"
              className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
              style={{ textDecoration: "none" }}
              onClick={onNavigate}
            >
              <span className="nav-icon">
                <Icon name="setting" />
              </span>
              {t("nav.settings")}
            </NavLink>
          </li>
          {isAdmin && (
            <li>
              <NavLink
                to="/app/admin"
                className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
                style={{ textDecoration: "none" }}
                onClick={onNavigate}
              >
                <span className="nav-icon">
                  <Icon name="info" />
                </span>
                {t("sidebar.admin")}
              </NavLink>
            </li>
          )}
        </ul>
        <div ref={accountMenuRef} className="sidebar-footer-wrap">
          <button
            type="button"
            className={
              "sidebar-footer sidebar-footer-trigger" +
              (accountMenuOpen ? " sidebar-footer-trigger--open" : "")
            }
            aria-expanded={accountMenuOpen}
            aria-haspopup="menu"
            aria-label={t("sidebar.accountMenuAria")}
            onClick={() => setAccountMenuOpen((open) => !open)}
          >
            <div className="avatar">{initials}</div>
            <div className="sidebar-footer-info">
              <div className="sidebar-footer-name">{user?.displayName ?? "Anna Wiśniewska"}</div>
              {isDemoUser ? (
                <span className="sidebar-footer-plan sidebar-footer-plan--demo">
                  {t("demo.footerBadge")}
                </span>
              ) : isPremium ? (
                <span className="sidebar-footer-plan sidebar-footer-plan--premium">
                  ✦ {t("sidebar.premium")}
                </span>
              ) : (
                <div className="sidebar-footer-plan">{t("sidebar.planFree")}</div>
              )}
            </div>
            <Icon
              name="chevron"
              size={14}
              className={
                "sidebar-footer-chevron" + (accountMenuOpen ? " sidebar-footer-chevron--open" : "")
              }
              aria-hidden
            />
          </button>
          {accountMenuOpen && (
            <div role="menu" className="sidebar-footer-menu">
              {user?.email && (
                <div className="sidebar-footer-menu-header">
                  <span className="sidebar-footer-menu-email">{user.email}</span>
                </div>
              )}
              <NavLink
                to="/app/settings"
                role="menuitem"
                className="sidebar-footer-menu-item"
                onClick={() => {
                  setAccountMenuOpen(false);
                  onNavigate?.();
                }}
              >
                <span className="sidebar-footer-menu-icon" aria-hidden>
                  <Icon name="setting" size={16} />
                </span>
                {t("nav.settings")}
              </NavLink>
              {showDevWebsiteOnboardingReset && (
                <>
                  <div className="sidebar-footer-menu-divider" role="separator" />
                  <button
                    type="button"
                    role="menuitem"
                    className="sidebar-footer-menu-item"
                    onClick={handleResetWebsiteOnboarding}
                  >
                    <span className="sidebar-footer-menu-icon" aria-hidden>
                      <Icon name="globe" size={16} />
                    </span>
                    {t("sidebar.devResetWebsiteOnboarding")}
                  </button>
                </>
              )}
              <div className="sidebar-footer-menu-divider" role="separator" />
              <button
                type="button"
                role="menuitem"
                className="sidebar-footer-menu-item sidebar-footer-menu-item--danger"
                onClick={() => {
                  setAccountMenuOpen(false);
                  onNavigate?.();
                  if (isDemoUser) {
                    void handleLeaveDemo();
                  } else {
                    void signOut();
                  }
                }}
              >
                <span className="sidebar-footer-menu-icon" aria-hidden>
                  <Icon name="logOut" size={16} />
                </span>
                {t("auth.signOut")}
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
